import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';
import { FormValidatorService } from '@main-client/src/app/form/form-validator.service';

import { DeleteMemberCategories } from '@libs/src/accounts/members.actions';
import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Group } from '@libs/src/models/group.model';
import { AppState } from '@main-client/src/app/app.interfaces';
import { parseInput } from '@main-client/src/app/hub-configuration/hub-configuration.helper';

import includes from 'lodash-es/includes';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-edit-hub-segmentation-items-drawer',
  templateUrl: './edit-hub-segmentation-items-drawer.component.html',
})
export class EditHubSegmentationItemsDrawerComponent implements OnInit {
  @Input() data: {
    group: Group;
    segmentationKey: string;
    segmentationName: string;
  };
  readonly categoriesLimit = 60;
  readonly viewModes = { edit: 'edit', success: 'success', summary: 'summary' };
  categoriesInput: FormControl<string>;
  isLoading = false;
  areCategoriesUpdated = false;
  viewMode = this.viewModes.edit;
  categoriesToDelete: any[] = [];
  categoriesToAdd: any[] = [];

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly formValidatorService: FormValidatorService,
    private readonly groupsBackend: GroupsBackend,
    private readonly store: Store<AppState>,
    private readonly toastService: ToastService,
    private readonly drawerRef: NzDrawerRef,
  ) {}

  ngOnInit() {
    const categoriesString = (this.data.group as any)[
      this.data.segmentationKey
    ].join('\n');
    this.categoriesInput = new FormControl(categoriesString, [
      this.formValidatorService.validMaxLines(this.categoriesLimit),
    ]);
    this.categoriesInput.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap((formValue) => {
          const { addedCategories, deletedCategories } =
            this.getCategoriesUpdate(
              formValue,
              this.data.group,
              this.data.segmentationKey,
            );
          this.areCategoriesUpdated =
            addedCategories.length || deletedCategories.length;
          this.categoriesToAdd = addedCategories;
          this.categoriesToDelete = deletedCategories;
        }),
      )
      .subscribe();
  }

  getCategoriesUpdate(input: string, group: Group, segmentationKey: string) {
    const parsedInput = parseInput(input);
    const addedCategories = parsedInput.filter(
      (item: string) => !includes((group as any)[segmentationKey], item),
    );
    const deletedCategories = (group as any)[segmentationKey].filter(
      (item: string) => !includes(parsedInput, item),
    );
    return { addedCategories, deletedCategories };
  }

  saveCategories(input: string, group: Group, segmentationKey: string) {
    this.isLoading = true;
    const { addedCategories, deletedCategories } = this.getCategoriesUpdate(
      input,
      this.data.group,
      this.data.segmentationKey,
    );
    const update = {
      [segmentationKey]: [
        ...(group as any)[segmentationKey].filter(
          (item: string) => !includes(deletedCategories, item),
        ),
        ...addedCategories,
      ],
    };
    return this.groupsBackend
      .patchGroup(group, update)
      .pipe(
        tap((response: Group) => {
          this.store.dispatch(
            DeleteMemberCategories({
              categories: deletedCategories,
              group: response,
              type: this.data.segmentationName,
            }),
          );
          this.isLoading = false;
          this.viewMode = this.viewModes.success;
        }),
        catchError((response: HttpErrorResponse) => {
          this.isLoading = false;
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
      )
      .subscribe();
  }

  closeDrawer(): void {
    this.drawerRef.close();
  }
}
